import type { Container } from 'pixi.js';
import type { DecorItem, FurnitureItem } from '@duck/contracts';
import { buildDecorItem } from './decor-catalog';
import type { ThemeArt } from './theme-art';

/**
 * SHARED object-placement primitives, reused by BOTH the level EDITOR and the in-game Furniture Store (ТЗ
 * §2 — "reuse the editor's placement; don't duplicate it"). Both surfaces let a user drop a {@link DecorItem}
 * onto the world: they share the grid {@link snap}, the {@link buildDecorItem} render builder, and the
 * {@link DecorItem} serialization shape. The editor mutates a local LevelDocument; the Store sends the
 * placement to the server (which owns the economy + persistence) — but the on-canvas placement mechanics
 * are identical and live here so there is one source of truth.
 */

/** Default placement grid (world units). The editor exposes a toggle; the Store snaps to this for tidy rooms. */
export const PLACEMENT_GRID = 20;

/** Snap a coordinate to the grid (grid ≤ 0 → round to the nearest integer). The single snap used by the
 * editor (re-exported from `editor-doc`) and the Store's placement ghost, so they can never drift. */
export function snap(v: number, grid: number): number {
  return grid > 0 ? Math.round(v / grid) * grid : Math.round(v);
}

/**
 * Build a translucent GHOST preview of a furniture item for the placement cursor — the exact piece that
 * will be placed, drawn from the SAME {@link buildDecorItem} builder the live world + editor use, so what
 * you see while dragging is what you get. The caller positions it (set `.x`/`.y`) each pointer move and
 * disposes it on cancel/confirm. `valid` dims it to a soft red when the spot can't take it (outside a
 * room), green-ish/normal otherwise — a dual cue alongside the DOM "click to place" prompt.
 */
export function buildFurnitureGhost(item: FurnitureItem, art: ThemeArt): Container {
  return buildGhostFromDecor(
    {
      id: 'ghost',
      kind: item.decorKind,
      x: 0,
      y: 0,
      ...(item.w !== undefined ? { w: item.w } : {}),
      ...(item.h !== undefined ? { h: item.h } : {}),
    },
    art,
  );
}

/**
 * Build a translucent ghost from a {@link DecorItem} (kind + optional size/rotation). Shared by the buy
 * ghost ({@link buildFurnitureGhost}) and the "Take"/move carry ghost (which carries an existing piece's
 * kind + rotation), so a moved piece previews exactly as it will land. The caller positions it each frame.
 */
export function buildGhostFromDecor(decor: DecorItem, art: ThemeArt): Container {
  const ghost = buildDecorItem({ ...decor, x: 0, y: 0 }, art);
  ghost.alpha = 0.6;
  return ghost;
}

/** Tint a ghost to signal whether the current spot is placeable: full (valid) vs a dim red wash (invalid). */
export function setGhostValid(ghost: Container, valid: boolean): void {
  ghost.alpha = valid ? 0.6 : 0.35;
  ghost.tint = valid ? 0xffffff : 0xff6b6b;
}
